import { useCallback, useMemo, useState } from 'react';
import { TeContainer } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';

import { Section } from '../../utils/styling';
import ViewContainer from '../ViewContainer';
import EmptyState from './Common/EmptyState';
import ServiceAgreementsTable from './ServiceAgreementsTable';
import useServiceAgreementsList from '../../hooks/serviceAgreements/useServiceAgreementsList';
import AddDealerModal from '../../pages/Settings/Dealers/AddDealerModal';

/**
 * Settings › Dealers: server-fetched Service Agreements with Providers. The
 * customer perspective this component once rendered moved to its own
 * server-paged page (ticket 02–04); the aggregated version retired with it
 * (ticket 05, CSSD-1649).
 */
function ServiceAgreementsList() {
  const { t } = useTranslation();

  const [openDealerDialog, setOpenDealerDialog] = useState<boolean>(false);

  const {
    visibleItems,
    totalItems,
    pagination,
    showAddCta,
    visibleItemsText,
    isFetching,
    isLoading,
    refresh,
  } = useServiceAgreementsList();

  const ctaLabel = useMemo(() => t('emptyState.dealers.noItems.cta'), [t]);

  const openModal = useCallback(() => setOpenDealerDialog(true), []);

  const closeDealerModal = useCallback(() => {
    setOpenDealerDialog(false);
    refresh();
  }, [refresh]);

  return (
    <>
      <TeContainer column fill grow alignItems="center" gap="xs" overflow="hidden">
        <Section $background="transparent">
          <ViewContainer
            title={t('dealers.title')}
            searchPlaceholder={t('dealers.search')}
            visibleItemsText={visibleItemsText}
            createNewItemText={t('dealers.add')}
            onCreateNewItemClick={openModal}
            buttonDisabled={isFetching || isLoading}
            noPadding
            // Hiding pagination for the dealers page, since each customer can have a MAX of 3 DEALERS
            // if this changes, please make sure to enable pagination for the dealers page as well
            pagination={{ ...pagination, enabled: false }}
            isLoadingData={isLoading || isFetching}
          >
            <Section $height="100%">
              {isFetching && <EmptyState kind="noItems" resource="dealers" hasLoader />}

              {!isFetching && (totalItems === 0 || visibleItems.length === 0) && (
                <EmptyState
                  kind={showAddCta ? 'noItems' : 'noSearch'}
                  resource="dealers"
                  ctaLabel={showAddCta ? ctaLabel : undefined}
                  onCtaClick={showAddCta ? openModal : undefined}
                />
              )}

              {!isFetching && visibleItems.length > 0 && (
                <ServiceAgreementsTable
                  agreements={visibleItems}
                  onClose={(shouldUpdate) => shouldUpdate && refresh()}
                />
              )}
            </Section>
          </ViewContainer>
        </Section>
      </TeContainer>

      {openDealerDialog && (
        <AddDealerModal
          open={openDealerDialog}
          onClose={closeDealerModal}
          onSubmit={() => refresh()}
        />
      )}
    </>
  );
}

export default ServiceAgreementsList;
